// GeoLocationMap.tsx - IP Geolocation visualization component
import React, { useState, useEffect, useMemo } from 'react';
import { geolocationAPI } from '../../services/api';

// Types
interface MapFeature {
  type: string;
  properties: {
    ip: string;
    hostname?: string;
    country: string;
    city: string;
    isp: string;
    open_ports?: number[];
    services?: string[];
    vuln_count?: number;
    severity?: string;
    scan_id?: number;
    target_id?: number;
  };
  geometry: {
    type: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
}

interface MapData {
  type: string;
  features: MapFeature[];
}

interface GeoLocationMapProps {
  scanId?: number;
  projectId?: number;
  reconScanId?: number;
  title?: string;
  showTable?: boolean;
  showWorldMap?: boolean;
  maxHeight?: string;
}

// Severity colors
const severityColors: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#ca8a04',
  low: '#2563eb',
  info: '#6b7280',
};

// Country flag emoji helper
const getCountryFlag = (countryCode: string): string => {
  if (!countryCode || countryCode.length !== 2) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const GeoLocationMap: React.FC<GeoLocationMapProps> = ({
  scanId,
  projectId,
  reconScanId,
  title = 'Target Locations',
  showTable = true,
  showWorldMap = true,
  maxHeight = '400px',
}) => {
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<MapFeature | null>(null);

  useEffect(() => {
    const loadMapData = async () => {
      setLoading(true);
      setError(null);

      try {
        let response;
        if (reconScanId) {
          response = await geolocationAPI.getReconScanMap(reconScanId);
        } else if (scanId) {
          response = await geolocationAPI.getScanMap(scanId);
        } else if (projectId) {
          response = await geolocationAPI.getProjectMap(projectId);
        } else {
          throw new Error('Either scanId, projectId, or reconScanId is required');
        }

        setMapData(response.data.map);
      } catch (err: any) {
        setError(err.message || 'Failed to load geolocation data');
        console.error('Geolocation error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadMapData();
  }, [scanId, projectId, reconScanId]);

  // Group features by country
  const countrySummary = useMemo(() => {
    if (!mapData?.features) return [];

    const countryMap = new Map<string, { count: number; vulns: number; features: MapFeature[] }>();

    mapData.features.forEach(feature => {
      const country = feature.properties.country || 'Unknown';
      const existing = countryMap.get(country) || { count: 0, vulns: 0, features: [] };
      existing.count++;
      existing.vulns += feature.properties.vuln_count || 0;
      existing.features.push(feature);
      countryMap.set(country, existing);
    });

    return Array.from(countryMap.entries())
      .map(([country, data]) => ({ country, ...data }))
      .sort((a, b) => b.count - a.count);
  }, [mapData]);

  // Simple SVG world map visualization
  const renderWorldMap = () => {
    if (!mapData?.features || mapData.features.length === 0) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
          backgroundColor: 'var(--color-text-primary)',
          borderRadius: '8px',
          color: 'var(--color-text-muted)',
        }}>
          <i className="fas fa-globe" style={{ marginRight: '0.5rem' }}></i>
          No geolocation data available
        </div>
      );
    }

    // Get bounds of all points
    const lons = mapData.features.map(f => f.geometry.coordinates[0]);
    const lats = mapData.features.map(f => f.geometry.coordinates[1]);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    // Map dimensions
    const width = 800;
    const height = 400;
    const padding = 40;

    // Convert lat/lon to SVG coordinates
    const projectPoint = (lon: number, lat: number): [number, number] => {
      const x = padding + ((lon + 180) / 360) * (width - 2 * padding);
      const y = padding + ((90 - lat) / 180) * (height - 2 * padding);
      return [x, y];
    };

    return (
      <div style={{
        backgroundColor: 'var(--color-surface-tertiary)',
        borderRadius: '8px',
        padding: '1rem',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: '100%', height: 'auto', maxHeight }}
        >
          {/* Grid lines */}
          {[-60, -30, 0, 30, 60].map(lat => {
            const [, y] = projectPoint(0, lat);
            return (
              <line
                key={`lat-${lat}`}
                x1={padding}
                y1={y}
                x2={width - padding}
                y2={y}
                stroke="var(--color-text-secondary)"
                strokeWidth="0.5"
                strokeDasharray="4,4"
              />
            );
          })}
          {[-120, -60, 0, 60, 120].map(lon => {
            const [x] = projectPoint(lon, 0);
            return (
              <line
                key={`lon-${lon}`}
                x1={x}
                y1={padding}
                x2={x}
                y2={height - padding}
                stroke="var(--color-text-secondary)"
                strokeWidth="0.5"
                strokeDasharray="4,4"
              />
            );
          })}

          {/* Data points */}
          {mapData.features.map((feature, index) => {
            const [x, y] = projectPoint(
              feature.geometry.coordinates[0],
              feature.geometry.coordinates[1]
            );
            const severity = feature.properties.severity || 'info';
            const color = severityColors[severity] || '#6b7280';
            const size = Math.min(12, 4 + (feature.properties.vuln_count || 0) * 0.5);

            return (
              <g key={`point-${index}`}>
                {/* Pulse animation for critical/high */}
                {(severity === 'critical' || severity === 'high') && (
                  <circle
                    cx={x}
                    cy={y}
                    r={size + 4}
                    fill={color}
                    opacity={0.3}
                  >
                    <animate
                      attributeName="r"
                      values={`${size};${size + 8};${size}`}
                      dur="2s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.3;0;0.3"
                      dur="2s"
                      repeatCount="indefinite"
                    />
                  </circle>
                )}
                <circle
                  cx={x}
                  cy={y}
                  r={size}
                  fill={color}
                  stroke="var(--color-text-inverse)"
                  strokeWidth="1"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedFeature(feature)}
                />
                {/* IP label for selected or if few points */}
                {(selectedFeature === feature || mapData.features.length <= 5) && (
                  <text
                    x={x}
                    y={y - size - 4}
                    fill="var(--color-text-inverse)"
                    fontSize="10"
                    textAnchor="middle"
                  >
                    {feature.properties.ip}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Legend */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginTop: '0.5rem',
          fontSize: '0.75rem',
          color: 'var(--color-text-muted)',
          flexWrap: 'wrap',
        }}>
          {Object.entries(severityColors).map(([sev, color]) => (
            <div key={sev} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block',
              }}></span>
              {sev.charAt(0).toUpperCase() + sev.slice(1)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Selected feature details panel
  const renderSelectedDetails = () => {
    if (!selectedFeature) return null;

    const { properties } = selectedFeature;
    const [lon, lat] = selectedFeature.geometry.coordinates;

    return (
      <div style={{
        backgroundColor: 'var(--color-text-primary)',
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '1rem',
        border: '1px solid var(--color-text-secondary)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem',
        }}>
          <h4 style={{ margin: 0, color: 'var(--color-text-inverse)' }}>
            <i className="fas fa-map-marker-alt" style={{ marginRight: '0.5rem', color: 'var(--color-primary-500)' }}></i>
            {properties.ip}
          </h4>
          <button
            onClick={() => setSelectedFeature(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '0.75rem',
          fontSize: '0.875rem',
        }}>
          {properties.hostname && (
            <div>
              <span style={{ color: 'var(--color-text-muted)' }}>Hostname:</span>
              <div style={{ color: 'var(--color-text-inverse)' }}>{properties.hostname}</div>
            </div>
          )}
          <div>
            <span style={{ color: 'var(--color-text-muted)' }}>Location:</span>
            <div style={{ color: 'var(--color-text-inverse)' }}>
              {properties.city}, {properties.country}
            </div>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-muted)' }}>Coordinates:</span>
            <div style={{ color: 'var(--color-text-inverse)' }}>
              {lat.toFixed(4)}, {lon.toFixed(4)}
            </div>
          </div>
          {properties.isp && (
            <div>
              <span style={{ color: 'var(--color-text-muted)' }}>ISP:</span>
              <div style={{ color: 'var(--color-text-inverse)' }}>{properties.isp}</div>
            </div>
          )}
          {properties.open_ports && properties.open_ports.length > 0 && (
            <div>
              <span style={{ color: 'var(--color-text-muted)' }}>Open Ports:</span>
              <div style={{ color: 'var(--color-text-inverse)' }}>{properties.open_ports.join(', ')}</div>
            </div>
          )}
          {properties.vuln_count !== undefined && (
            <div>
              <span style={{ color: 'var(--color-text-muted)' }}>Findings:</span>
              <div style={{
                color: severityColors[properties.severity || 'info'],
                fontWeight: 600,
              }}>
                {properties.vuln_count} ({properties.severity || 'info'})
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: '0.75rem' }}>
          <a
            href={`https://www.google.com/maps?q=${lat},${lon}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--color-primary-500)',
              fontSize: '0.75rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <i className="fas fa-external-link-alt"></i>
            Open in Google Maps
          </a>
        </div>
      </div>
    );
  };

  // Country summary table
  const renderCountryTable = () => {
    if (countrySummary.length === 0) return null;

    return (
      <div style={{
        backgroundColor: 'var(--color-text-primary)',
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '1rem',
      }}>
        <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--color-text-inverse)' }}>
          <i className="fas fa-flag" style={{ marginRight: '0.5rem', color: 'var(--color-primary-500)' }}></i>
          Targets by Country
        </h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-text-secondary)' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--color-text-muted)' }}>Country</th>
                <th style={{ textAlign: 'center', padding: '0.5rem', color: 'var(--color-text-muted)' }}>Targets</th>
                <th style={{ textAlign: 'center', padding: '0.5rem', color: 'var(--color-text-muted)' }}>Findings</th>
              </tr>
            </thead>
            <tbody>
              {countrySummary.slice(0, 10).map(({ country, count, vulns }) => (
                <tr key={country} style={{ borderBottom: '1px solid var(--color-text-secondary)' }}>
                  <td style={{ padding: '0.5rem', color: 'var(--color-text-inverse)' }}>{country}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem', color: 'var(--color-text-inverse)' }}>{count}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem', color: vulns > 0 ? 'var(--color-warning)' : 'var(--color-text-muted)' }}>
                    {vulns}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'var(--color-text-primary)',
        borderRadius: '12px',
        padding: '1.5rem',
        textAlign: 'center',
        color: 'var(--color-text-muted)',
      }}>
        <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
        Loading geolocation data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'var(--color-text-primary)',
        borderRadius: '12px',
        padding: '1.5rem',
        textAlign: 'center',
        color: 'var(--color-error)',
      }}>
        <i className="fas fa-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
        {error}
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'var(--color-text-primary)',
      borderRadius: '12px',
      padding: '1.5rem',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
      }}>
        <h3 style={{ margin: 0, color: 'var(--color-text-inverse)' }}>
          <i className="fas fa-globe-americas" style={{ marginRight: '0.5rem', color: 'var(--color-primary-500)' }}></i>
          {title}
        </h3>
        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
          {mapData?.features.length || 0} locations
        </span>
      </div>

      {showWorldMap && renderWorldMap()}
      {renderSelectedDetails()}
      {showTable && renderCountryTable()}
    </div>
  );
};

export default GeoLocationMap;
